/*************************************************************************
 *
 * c20g CONFIDENTIAL
 * __________________
 *
 *  [2007] - [2023] Counterpoint Consulting, Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Counterpoint Consulting Incorporated.
 * The intellectual and technical concepts contained
 * herein are proprietary to Counterpoint Consulting Incorporated
 * and its suppliers and may be covered by U.S. and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Counterpoint Consulting Incorporated.
 */

import _ from 'lodash';
import Radio from 'backbone.radio';

import GridView from '@/views/GridView';
import StringCell from '@/backgrid/StringCell';
import ButtonCell from '@/backgrid/ButtonCell';
import PageableCollection from '@/backbone/PageableCollection';
import showModal from '@/util/showModal';
import showSuccessMessage from '@/util/ShowSuccessMessage';
import showErrorMessage from '@/util/showErrorMessage';
import generateCSV from '@/util/generateCSV';
import downloadCSV from '@/util/downloadCSV';
import DeliveryScheduleModalView from '@/views/DeliveryScheduleModalView';
import SaveDeliverySchedules from '@/services/SaveDeliverySchedules';

import HorizontalAddressCell from '../HorizontalAddressCell.js/HorizontalAddressCell';
import OrderStatuses from '@/enums/OrderStatuses';

const DeliveryScheduleGridView = GridView.extend({
  className: 'delivery-schedule-grid-view horizontal-scroll',

  childEvents: {
    'next:clicked': 'onNextClicked',
    'previous:clicked': 'onPreviousClicked'
  },

  initialize() {
    const self = this;
    const hasAdditionalServices = this.model.get('additionalServices')?.length > 0;
    const hasAdditionalAccessories = this.model.get('additionalAccessories')?.length > 0;
    let orderTypeId;

    const orderDetails = this.model.get('orderDetails');
    const isSubmitted = !orderDetails || orderDetails.orderDetails.statusId !== OrderStatuses.INTERNAL_DRAFT;
    const showInternalSubmittedFields = !EXTERNAL && isSubmitted;
    this.model.set('showInternalSubmittedFields', showInternalSubmittedFields);

    if (orderDetails) {
      const defaultNote = Radio.channel('order').request('get:defaultNote');
      this.model.set('note', defaultNote);
      orderTypeId = orderDetails.orderTypeId;
    } else if (this.model.get('orderPackage')) {
      orderTypeId = this.model.get('orderPackage').orderFormTypeID;
    } else {
      orderTypeId = this.model.get('awPackage').orderFormTypeId;
    }
    this.model.set('orderTypeId', orderTypeId);
    const columns = [
      {
        name: 'trackerNumber',
        label: 'Tracker #',
        cell: StringCell,
        headerCell: 'custom',
        width: 10,
        editable: false,
        downloadable: showInternalSubmittedFields,
        renderable: showInternalSubmittedFields
      },
      {
        name: 'clin',
        label: showInternalSubmittedFields ? 'IDIQ CLIN' : 'CLIN',
        cell: StringCell,
        headerCell: 'custom',
        width: 10,
        editable: false,
        downloadable: true,
        direction: 'ascending',
        renderable: showInternalSubmittedFields
      },
      {
        name: 'equipmentName',
        label: 'Equipment',
        cell: StringCell,
        headerCell: 'custom',
        width: 30,
        editable: false,
        downloadable: true
      },
      {
        name: 'volumeBand',
        label: 'Volume Band',
        cell: StringCell,
        headerCell: 'custom',
        renderable: false,
        downloadable: true
      },
      {
        name: 'niprText',
        label: 'NIPR or SIPR',
        cell: StringCell,
        headerCell: 'custom',
        renderable: false,
        downloadable: true
      },
      {
        name: 'colorText',
        label: 'Color or Monochrome (B&W)',
        cell: StringCell,
        headerCell: 'custom',
        renderable: false,
        downloadable: true
      },
      {
        name: 'additionalServiceText',
        label: 'Additional Services',
        cell: StringCell,
        headerCell: 'custom',
        width: 30,
        editable: false,
        downloadable: true,
        renderable: hasAdditionalServices
      },
      {
        name: 'additionalAccessoryText',
        label: 'Additional Accessories',
        cell: StringCell,
        headerCell: 'custom',
        width: 30,
        editable: false,
        downloadable: true,
        renderable: hasAdditionalAccessories
      },
      {
        name: 'optionalAccessories',
        label: 'Optional Accessories',
        cell: StringCell,
        headerCell: 'custom',
        renderable: false,
        downloadable: true
      },

      {
        name: 'network',
        label:
          orderTypeId === '21' ? 'Network (Examples: NMCI, EDU, MCEN, ONENET)' : 'Network (Examples: NMCI, EDU, MCEN)',
        cell: StringCell,
        headerCell: 'custom',
        width: 10,
        editable: false,
        downloadable: true
      },
      {
        name: 'commandName',
        label: 'Organization/Unit',
        cell: StringCell,
        headerCell: 'custom',
        width: 20,
        editable: false,
        downloadable: true
      },
      {
        name: 'branchName',
        label: 'Branch of Service',
        cell: StringCell,
        headerCell: 'custom',
        width: 15,
        editable: false,
        downloadable: true
      },
      {
        name: 'commandEchelon',
        label: 'Command/Echelon',
        cell: StringCell,
        headerCell: 'custom',
        width: 20,
        editable: false,
        downloadable: true
      },
      {
        name: 'address',
        label: 'Address',
        cell: HorizontalAddressCell,
        headerCell: 'custom',
        width: 35,
        editable: false,
        sortable: true,
        downloadable: false
      },
      {
        name: 'completeAddress1',
        label:
          'Complete Address as Follows: 1st Street Address/2nd Building & Floor Number/3rd Room & Pole Number (No Commas or Punctuation)',
        editable: false,
        cell: StringCell,
        headerCell: 'custom',
        width: 20,
        renderable: false,
        downloadable: true
      },
      {
        name: 'completeAddress2',
        label: 'Complete Address as Follows: 1st City/2nd State/3rd ZIP (No Commas or Punctuation)',
        editable: false,
        cell: StringCell,
        headerCell: 'custom',
        width: 20,
        renderable: false,
        downloadable: true
      },
      {
        name: 'buildingFloorNumber',
        label: 'Building',
        cell: StringCell,
        headerCell: 'custom',
        width: 15,
        renderable: false,
        downloadable: true
      },
      {
        name: 'floorNumber',
        label: 'Floor Number',
        cell: StringCell,
        headerCell: 'custom',
        width: 15,
        renderable: false,
        downloadable: true
      },
      {
        name: 'roomNumber',
        label: 'Room Number',
        cell: StringCell,
        headerCell: 'custom',
        width: 15,
        renderable: false,
        downloadable: true
      },
      {
        name: 'poleNumber',
        label: 'Pole Number',
        cell: StringCell,
        headerCell: 'custom',
        width: 15,
        renderable: false,
        downloadable: true
      },
      {
        name: 'suite',
        label: 'Suite',
        cell: StringCell,
        headerCell: 'custom',
        width: 15,
        renderable: false,
        downloadable: true
      },
      {
        name: 'addressLine1',
        label: 'Address 1',
        cell: StringCell,
        headerCell: 'custom',
        width: 15,
        renderable: false,
        downloadable: true
      },
      {
        name: 'addressLine2',
        label: 'Address 2',
        cell: StringCell,
        headerCell: 'custom',
        width: 15,
        renderable: false,
        downloadable: true
      },
      {
        name: 'city',
        label: 'City/Base',
        cell: StringCell,
        headerCell: 'custom',
        width: 15,
        renderable: false,
        downloadable: true
      },
      {
        name: 'regionCode',
        label: 'State',
        cell: StringCell,
        headerCell: 'custom',
        width: 15,
        renderable: false,
        downloadable: true
      },
      {
        name: 'zip',
        label: 'Zip',
        cell: StringCell,
        headerCell: 'custom',
        width: 15,
        renderable: false,
        downloadable: true
      },
      {
        name: 'deliveryPOCName',
        label: 'Delivery Contact Name',
        cell: StringCell,
        headerCell: 'custom',
        width: 15,
        editable: false,
        downloadable: true
      },
      {
        name: 'deliveryPOCEmail',
        label: 'Delivery Contact Email',
        cell: StringCell,
        headerCell: 'custom',
        width: 15,
        editable: false,
        renderable: false,
        downloadable: true
      },
      {
        name: 'deliveryPOCPhone',
        label: 'Delivery Contact Phone',
        cell: StringCell,
        headerCell: 'custom',
        width: 15,
        editable: false,
        renderable: false,
        downloadable: true
      },
      {
        name: 'itPOCName',
        label: 'IT Contact Name',
        cell: StringCell,
        headerCell: 'custom',
        width: 15,
        editable: false,
        renderable: !!this.options.itPOCExists,
        downloadable: !!this.options.itPOCExists
      },
      {
        name: 'itPOCEmail',
        label: 'IT Contact Email',
        cell: StringCell,
        headerCell: 'custom',
        width: 15,
        editable: false,
        renderable: false,
        downloadable: true
      },
      {
        name: 'itPOCPhone',
        label: 'IT Contact Phone',
        cell: StringCell,
        headerCell: 'custom',
        width: 15,
        editable: false,
        renderable: false,
        downloadable: true
      },
      {
        name: 'notes',
        label: 'Notes/Message',
        cell: StringCell,
        headerCell: 'custom',
        width: 15,
        renderable: false,
        downloadable: true
      },
      {
        name: 'editViewButton',
        label: '',
        width: 7,
        headerCell: 'custom',
        cell: ButtonCell.extend({
          name() {
            if (self.options.editable) {
              return 'Edit';
            }
            return 'View';
          },
          onClick(e, model) {
            self.options.editable
              ? self.onClickEdit(e, model, self.options.objectTypeCode)
              : self.onClickView(e, model, self.options.objectTypeCode);
          },
          buttonClass: 'btn-sm btn-outline-primary text-center'
        }),
        editable: false,
        sortable: false
      }
    ];

    if (this.options.objectTypeCode === 'AW_PACKAGE') {
      columns.splice(1, 0, {
        name: 'lineClin',
        label: 'Line CLIN',
        cell: StringCell,
        headerCell: 'custom',
        width: 10,
        editable: false,
        downloadable: true,
        renderable: !EXTERNAL
      });
    }
    if (orderTypeId !== '20' && orderTypeId !== '21') {
      columns.splice(
        10,
        0,
        {
          name: 'isReplacementText',
          label: 'New or Replacement',
          cell: StringCell,
          headerCell: 'custom',
          width: 10,
          editable: false,
          downloadable: true
        },
        {
          name: 'termingSerialNumber',
          label: 'Terming Serial Number',
          cell: StringCell,
          headerCell: 'custom',
          width: 10,
          editable: false,
          downloadable: true
        }
      );
      if (orderTypeId !== '5' && orderTypeId !== '17') {
        columns.splice(13, 0, {
          name: 'trainingTypeText',
          label: 'Training',
          cell: StringCell,
          headerCell: 'custom',
          width: 10,
          editable: false,
          downloadable: true
        });
      }
    }
    this.options.columns = columns;
    this.options.columns,
      (this.options.collection = new PageableCollection(this.model.get('schedules'), {
        pageSize: 50
      }));
    this.options.emptyText = 'No Devices Selected';
    this.options.width = '3000px';
    GridView.prototype.initialize?.apply(this, this.options);
  },

  openPreviousItem(modalData) {
    let e;
    const newModel = this.options.collection.fullCollection.models[modalData.nextIndex];
    const objectTypeCode = this.options.objectTypeCode;
    if (this.options.editable) {
      this.onClickEdit(e, newModel, objectTypeCode);
    } else {
      this.onClickView(e, newModel, objectTypeCode);
    }
  },

  openNextItem(modalData) {
    let e;
    const newModel = this.options.collection.fullCollection.models[modalData.nextIndex];
    const objectTypeCode = this.options.objectTypeCode;
    if (this.options.editable) {
      this.onClickEdit(e, newModel, objectTypeCode);
    } else {
      this.onClickView(e, newModel, objectTypeCode);
    }
  },

  async saveRowItem(modalData, model) {
    // Manually find region, additional service, and training names from lookup data
    const region = _.find(this.model.get('regions'), (x) => {
      return x.id === modalData.formData.address.regionId;
    });
    modalData.formData.address.regionName = region?.name;
    modalData.formData.address.regionCode = region?.code;

    const service = _.find(this.model.get('additionalServices'), (x) => {
      return x.id === modalData.formData.additionalServiceId;
    });
    modalData.formData.additionalServiceText = service?.name;

    const accessory = _.find(this.model.get('additionalAccessories'), (x) => {
      return x.id === modalData.formData.additionalAccessoryId;
    });
    modalData.formData.additionalAccessoryText = accessory?.name;

    const training = _.find(this.model.get('trainingTypes'), (x) => {
      return x.id === modalData.formData.trainingTypeId;
    });
    modalData.formData.trainingTypeText = training?.name;

    // Set row model data in the UI
    if (modalData.formData.address.zip) {
      modalData.formData.address.isConus = '1';
    }
    model.set('termingSerialNumber', modalData.formData.termingSerialNumber);
    model.set('isReplacementText', modalData.formData.isReplacement === '1' ? 'Replacement' : 'New');
    model.set('network', modalData.formData.network);
    model.set('trainingTypeText', modalData.formData.trainingTypeText);
    model.set('additionalServiceText', modalData.formData.additionalServiceText);
    model.set('additionalAccessoryText', modalData.formData.additionalAccessoryText);

    model.set('commandEchelon', modalData.formData.commandEchelon);
    model.set('address', modalData.formData.address);

    model.set('deliveryPOCName', modalData.formData.deliveryPOCName);
    model.set('deliveryPOCEmail', modalData.formData.deliveryPOCEmail);
    model.set('deliveryPOCPhone', modalData.formData.deliveryPOCPhone);
    const deliveryNames = model.get('deliveryPOCName').split(' ');
    const deliveryPOCGivenNames = deliveryNames
      .slice(0, deliveryNames.length <= 2 ? 1 : deliveryNames.length - 1)
      .join(' ');
    const deliveryPOCLastName = deliveryNames
      .slice(deliveryNames.length > 1 ? deliveryNames.length - 1 : deliveryNames.length)
      .join(' ');
    model.set('deliveryPOCFirstName', deliveryPOCGivenNames);
    model.set('deliveryPOCLastName', deliveryPOCLastName);

    if (this.options.itPOCExists) {
      model.set('itPOCName', modalData.formData.itPOCName);
      model.set('itPOCEmail', modalData.formData.itPOCEmail);
      model.set('itPOCPhone', modalData.formData.itPOCPhone);
      const itNames = model.get('itPOCName').split(' ');
      const itPOCGivenNames = itNames.slice(0, itNames.length <= 2 ? 1 : itNames.length - 1).join(' ');
      const itPOCLastName = itNames.slice(itNames.length > 1 ? itNames.length - 1 : itNames.length).join(' ');
      model.set('itPOCFirstName', itPOCGivenNames);
      model.set('itPOCLastName', itPOCLastName);
    }

    const index = _.findIndex(this.model.get('schedules'), ['id', model.get('id')]);
    this.model.get('schedules')[index] = model.toJSON();
    const deliverySchedules = [model.toJSON()];

    try {
      await SaveDeliverySchedules(this.options.objectID, this.options.objectTypeCode, deliverySchedules);

      showSuccessMessage({
        code: 'saveSuccess',
        title: 'Save Success',
        message: 'Delivery Schedule Saved.',
        hideAfter: 1
      });
    } catch (err) {
      showErrorMessage({
        errorCode: 'saveDeliveryScheduleError',
        errorTitle: 'Save Error',
        errorMessage:
          'An error occurred while saving this line item in the delivery schedule. Please try again or contact an administrator.'
      });
    }
  },

  async onClickEdit(e, model, objectTypeCode) {
    if (e?.type) {
      e.preventDefault();
    }
    if (this.model.get('note')) model.set('note', this.model.get('note'));
    model.set('regions', this.model.get('regions'));
    model.set('commandPOCs', this.model.get('commandPOCs'));
    model.set('trainingTypes', this.model.get('trainingTypes'));
    model.set('isReplacementOptions', this.model.get('isReplacementOptions'));
    model.set('additionalServices', this.model.get('additionalServices'));
    model.set('additionalAccessories', this.model.get('additionalAccessories'));
    model.set('isOtherCountry', this.model.get('isOtherCountry'));
    model.set('isMFD', this.model.get('isMFD'));
    model.set('showInternalSubmittedFields', this.model.get('showInternalSubmittedFields'));
    model.set('orderTypeID', this.model.get('orderTypeId'));
    model.set('conusOconusRegions', this.model.get('conusOconusRegions'));
    await showModal({
      title: 'Delivery Schedule Details',
      view: new DeliveryScheduleModalView({
        objectTypeCode,
        isReadOnly: false,
        itPOCExists: !!this.options.itPOCExists,
        model
      }),
      dialogClasses: 'modal-lg'
    }).then((modalData) => {
      _.each(Object.keys(modalData.formData), (key) => {
        model.set(key, modalData.formData[key]);
      });

      if (modalData.button === 'prev') {
        this.saveRowItem(modalData, model);
        this.openPreviousItem(modalData);
      } else if (modalData.button === 'next') {
        this.saveRowItem(modalData, model);
        this.openNextItem(modalData);
      } else if (modalData.button === 'save') {
        this.saveRowItem(modalData, model);
      }
    });
  },

  async onClickView(e, model, objectTypeCode) {
    if (e?.type) {
      e.preventDefault();
    }
    if (this.model.get('note')) model.set('note', this.model.get('note'));
    model.set('regions', this.model.get('regions'));
    model.set('trainingTypes', this.model.get('trainingTypes'));
    model.set('isReplacementOptions', this.model.get('isReplacementOptions'));
    model.set('additionalServices', this.model.get('additionalServices'));
    model.set('additionalAccessories', this.model.get('additionalAccessories'));
    model.set('isMFD', this.model.get('isMFD'));
    model.set('showInternalSubmittedFields', this.model.get('showInternalSubmittedFields'));
    model.set('orderTypeID', this.model.get('orderTypeId'));
    model.set('conusOconusRegions', this.model.get('conusOconusRegions'));
    const modalData = await showModal({
      title: 'Delivery Schedule Details',
      view: new DeliveryScheduleModalView({
        objectTypeCode,
        isReadOnly: true,
        itPOCExists: !!this.options.itPOCExists,
        model
      }),
      dialogClasses: 'modal-lg'
    });
    if (modalData.button === 'prev') {
      this.openPreviousItem(modalData);
    } else if (modalData.button === 'next') {
      this.openNextItem(modalData);
    }
  },

  getCSVColumns() {
    return this.options.columns.toJSON()
      .filter(column => column.downloadable && column.label)
      .map(({ name, label }) => ({ name, label }));
  },

  async downloadTemplateCSV() {
    const columnsToInclude = this.getCSVColumns();
    const csvContent = await generateCSV([], columnsToInclude);
    const fileName = `${this.options.objectTypeCode.toLowerCase()}_${this.options.objectID}_delivery_schedule_template`;
    await downloadCSV(csvContent, fileName);
  },

  async generateAndDownloadCSV() {
    const note = this.model.get('note');
    try {
      const columnsToInclude = this.getCSVColumns();

      //add data
      const data = this.options.collection.fullCollection.toJSON();
      _.each(data, (row) => {
        Object.assign(row, row.address);
        row.completeAddress1 = [
          row.addressLine1,
          row.addressLine2,
          row.buildingFloorNumber,
          row.floorNumber,
          row.roomPoleNumber
        ]
          .filter((x) => x)
          .join(' ');
        row.completeAddress2 = [row.city, row.regionCode, row.zip].filter((x) => x).join(' ');
        row.volumeBand = row.device.volumeBand;
        row.notes = note ? note : row.note;
      });

      const csvContent = await generateCSV(data, columnsToInclude);
      const fileName = `${this.options.objectTypeCode.toLowerCase()}_${this.options.objectID}_delivery_schedule`;

      //download
      await downloadCSV(csvContent, fileName);
    } catch (err) {
      showErrorMessage({
        errorCode: 'downloadError',
        errorTitle: 'Download Error',
        errorMessage: 'And error occurred while downloading delivery schedule CSV.'
      });
    }
  },

  removePunctuation(val) {
    if (!val) return;
    return val.replace(/[!"#$%&'()*+,-./:;<=>?@[\]^_`{|}~]/g, '');
  }
});

export default DeliveryScheduleGridView;
